import { defineStore } from 'pinia'
import { doc, getDoc, setDoc } from 'firebase/firestore'
import { useNuxtApp } from '#app'
 
export interface Banner {
    tag: string
    title: string
    description: string
    image: string
    link: string
}

export interface HomeCMS {
    hero: {
        title: string
        subtitle: string
        description: string
        image: string
        primaryBtnText: string
        primaryBtnLink: string
        secondaryBtnText: string
        secondaryBtnLink: string
    }
    services: {
        title: string
        description: string
        blocks: {
            title: string
            description: string
            icon: string
            link: string
        }[]
    }
    trustBadges: {
        badges: {
            title: string
            subtitle: string
            icon: string
        }[]
    }
    footer: {
        aboutText: string
        address: string
        phone: string
        mobile: string
        email: string
        socials: {
            facebook: string
            twitter: string
            linkedin: string
            instagram: string
        }
    }
    banners: Banner[]
}

const defaultCMS: HomeCMS = {
    hero: {
        title: "Uncompromising Protection.",
        subtitle: "Next-Gen Fire Safety",
        description: "Industrial-grade equipment and advanced suppression systems, engineered for zero-failure reliability.",
        image: "https://images.unsplash.com/photo-1599839619722-39751411ea63?auto=format&fit=crop&q=80&w=2500",
        primaryBtnText: "Explore Shop",
        primaryBtnLink: "/shop",
        secondaryBtnText: "Request Consultation",
        secondaryBtnLink: "/contact"
    },
    services: {
        title: "End-to-End Protection",
        description: "We don't just sell equipment; our certified engineers design, install, and maintain comprehensive life safety solutions.",
        blocks: [
            {
                title: "System Installation",
                description: "Expert installation of fire alarms, suppressors, and sprinkler networks by licensed technicians ensuring NFPA compliance.",
                icon: "Settings",
                link: "/services"
            },
            {
                title: "Inspection & Audits",
                description: "Routine maintenance, rigorous safety audits, and code compliance checks to guarantee system readiness at all times.",
                icon: "ClipboardCheck",
                link: "/services"
            },
            {
                title: "Safety Training",
                description: "Comprehensive fire safety certification for your staff, including live-fire extinguisher training and evacuation planning.",
                icon: "GraduationCap",
                link: "/services"
            }
        ]
    },
    trustBadges: {
        badges: [
            { title: "NFPA Certified", subtitle: "Quality Compliant", icon: "ShieldCheck" },
            { title: "Same Day Ship", subtitle: "Fast Dispatch", icon: "Truck" },
            { title: "24/7 Support", subtitle: "Expert Help", icon: "Tool" },
            { title: "5-Year Warranty", subtitle: "Extended Coverage", icon: "Medal" }
        ]
    },
    footer: {
        aboutText: "Your trusted partner in fire protection. We provide certified fire safety equipment for residential, commercial, and industrial use.",
        address: "Suhaim Tower, Al-Sadd, Doha, Qatar",
        phone: "+974 4140 0922",
        mobile: "+974 3081 2223",
        email: "info@alarmsupplies.qa",
        socials: {
            facebook: "#",
            twitter: "#",
            linkedin: "#",
            instagram: "#"
        }
    },
    banners: [
      {
        tag: "Precision Engineering",
        title: "Advanced Fire Detection Systems",
        description: "Integrating state-of-the-art sensory technology for absolute reliability in industrial environments.",
        image: "https://images.unsplash.com/photo-1599839619722-39751411ea63?auto=format&fit=crop&q=80&w=2000",
        link: "/category/fire-alarms"
      },
      {
        tag: "Safety Excellence",
        title: "Industrial Grade Protection",
        description: "Meeting international standards for fire safety, ensuring maximum protection for your infrastructure.",
        image: "https://images.unsplash.com/photo-1517094857443-40e3558197f4?auto=format&fit=crop&q=80&w=2000",
        link: "/category/fire-extinguishers"
      },
      {
        tag: "24/7 Monitoring",
        title: "Intelligent Alert Management",
        description: "Always connected, always responsive. The future of fire safety management is here.",
        image: "https://images.unsplash.com/photo-1581092160562-40aa08e78837?auto=format&fit=crop&q=80&w=2000",
        link: "/shop"
      }
    ]
}

export const useCMSStore = defineStore('cms', {
    state: () => ({
        home: defaultCMS as HomeCMS,
        loading: false
    }),

    actions: {
        async fetchHomeCMS() {
            this.loading = true
            try {
                if (process.client) {
                    const { $db } = useNuxtApp() as any
                    const docRef = doc($db, 'config', 'homepage')
                    const docSnap = await getDoc(docRef)
                    
                    if (docSnap.exists()) {
                        this.home = { ...defaultCMS, ...docSnap.data() } as HomeCMS
                    } else {
                        // Initialize with defaults if not exists
                        await setDoc(docRef, defaultCMS)
                        this.home = defaultCMS
                    }
                }
            } catch (err) {
                console.error('Error fetching Home CMS:', err)
            } finally {
                this.loading = false
            }
        },

        async updateHomeCMS(data: HomeCMS) {
            try {
                if (process.client) {
                    const { $db } = useNuxtApp() as any
                    const docRef = doc($db, 'config', 'homepage')
                    await setDoc(docRef, data)
                    this.home = data
                }
            } catch (err) {
                console.error('Error updating Home CMS:', err)
                throw err
            }
        }
    }
})
